export const ARP_RATE_OPTIONS = Object.freeze([
  Object.freeze({ value: 4, label: '1/1' }),
  Object.freeze({ value: 2, label: '1/2' }),
  Object.freeze({ value: 1.5, label: '1/4 D' }),
  Object.freeze({ value: 1, label: '1/4' }),
  Object.freeze({ value: 0.75, label: '1/8 D' }),
  Object.freeze({ value: 2 / 3, label: '1/4 T' }),
  Object.freeze({ value: 0.5, label: '1/8' }),
  Object.freeze({ value: 0.375, label: '1/16 D' }),
  Object.freeze({ value: 1 / 3, label: '1/8 T' }),
  Object.freeze({ value: 0.25, label: '1/16' }),
  Object.freeze({ value: 1 / 6, label: '1/16 T' }),
  Object.freeze({ value: 0.125, label: '1/32' }),
  Object.freeze({ value: 1 / 12, label: '1/32 T' }),
  Object.freeze({ value: 0.0625, label: '1/64' })
]);

export const ARP_RATE_VALUES = Object.freeze(ARP_RATE_OPTIONS.map(({ value }) => value));

/** Snap legacy/free-rate values to the closest supported musical division. */
const STRAIGHT_RATE_VALUES = Object.freeze([4, 2, 1, 0.5, 0.25, 0.125, 0.0625]);

export function normalizeArpRate(value, fallback = 0.25) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  // Dotted and triplet divisions are chosen exactly; anything else (legacy
  // free rates) snaps to the nearest straight division, as it always has.
  const exact = ARP_RATE_VALUES.find((candidate) => Math.abs(candidate - numeric) < 1e-6);
  if (exact !== undefined) return exact;
  return STRAIGHT_RATE_VALUES.reduce((closest, candidate) => (
    Math.abs(candidate - numeric) < Math.abs(closest - numeric) ? candidate : closest
  ), STRAIGHT_RATE_VALUES[0]);
}

/** Rate values are quarter-note beat lengths: 1 = 1/4, .5 = 1/8, etc. */
export function arpRateToMilliseconds(rate, bpm = 120) {
  const safeBpm = Math.max(30, Number(bpm) || 120);
  return (60000 / safeBpm) * normalizeArpRate(rate);
}

/** Gate length of one arpeggiated step, in milliseconds. */
export function arpGateMilliseconds(rate, bpm = 120, gate = 0.7) {
  const stepMs = arpRateToMilliseconds(rate, bpm);
  const safeGate = Number.isFinite(Number(gate)) ? Math.min(1, Math.max(0.05, Number(gate))) : 0.7;
  return Math.max(20, stepMs * safeGate);
}

/**
 * Length in beats to record for one arpeggiated step.
 *
 * The arp generates its notes on a timer rather than through the normal
 * note-on path, so nothing captured them into the take — the performance was
 * audible live but absent from the clip, and therefore from every export.
 * Recording needs the step's gate expressed in the timeline's beat domain.
 */
export function arpGateBeats(rate, bpm = 120, gate = 0.7) {
  const safeBpm = Math.max(30, Number(bpm) || 120);
  return (arpGateMilliseconds(rate, bpm, gate) / 1000) * (safeBpm / 60);
}
